/**
 * De brug tussen `texturen.ts` en de scène.
 *
 * Twee dingen die alleen hier kunnen: de anisotropie ophalen (die hangt van de
 * GPU af, dus er moet een renderer zijn) en het resultaat memoïseren op de
 * Studio-instellingen (dus er moet een React-boom zijn).
 *
 * Staan de texturen uit, dan komt er `null` terug en niet een set vlakke maps.
 * Dat is het verschil tussen "geen textuur" en "een textuur die niets doet":
 * de eerste kost geen geheugen en geen sample per pixel.
 */
import { useMemo } from 'react'
import { useThree } from '@react-three/fiber'
import { oppervlakken, zetAnisotropie, type Oppervlakken } from './texturen'
import { useMagazijnConfig, useOppervlak } from '../scene/store'

export function useOppervlakken(): Oppervlakken | null {
  const opp = useOppervlak()
  const cfg = useMagazijnConfig()
  const gl = useThree((s) => s.gl)

  return useMemo(() => {
    if (!opp.texturen) return null
    const set = oppervlakken(opp.textuurDetail, cfg.seed)
    zetAnisotropie(set, gl.capabilities.getMaxAnisotropy())
    return set
  }, [opp.texturen, opp.textuurDetail, cfg.seed, gl])
}

/**
 * Materiaal-props voor een oppervlak, of leeg als de texturen uit staan.
 *
 * `normalScale` is een `Vector2` en moet als tuple aan R3F: de reliëfschuif
 * werkt daarop en niet op de normal map zelf, zodat één gedeelde map door de
 * hele scène gebruikt kan worden met een verschillende sterkte per materiaal.
 */
export function textuurProps(
  set: Oppervlakken | null,
  soort: keyof Oppervlakken,
  relief: number,
  sterkte = 1,
) {
  if (!set) return {}
  const o = set[soort]
  return {
    map: o.map,
    roughnessMap: o.roughnessMap,
    normalMap: o.normalMap,
    normalScale: [relief * sterkte, relief * sterkte] as [number, number],
  }
}
